import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 15000 });

// Bearer-token holder: useAuth keeps it up to date via setAuthToken.
// `_authReady` lets the request interceptor briefly wait during cold start
// for the Supabase session to rehydrate — avoiding the first-paint race
// where `/api/*` requests fire before the token arrives (getting a 401
// from the backend that then forces a sign-out).
let _authToken = null;
let _authReady = false;  // true once useAuth has decided (token present OR explicitly none)
export const setAuthToken = (tok) => {
  _authToken = tok || null;
  _authReady = true;
};
export const getAuthTokenForFallback = () => _authToken;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

client.interceptors.request.use(async (cfg) => {
  const url = cfg.url || "";
  // Auth endpoints must NOT wait on themselves — they are how the token arrives.
  const isAuthEndpoint = url.startsWith("/auth") || url.includes("/auth/");
  if (!isAuthEndpoint && !_authReady) {
    // Wait up to 2 s for useAuth to call setAuthToken. Polling tick = 50 ms.
    const deadline = Date.now() + 2000;
    while (!_authReady && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(50);
    }
  }
  if (_authToken) cfg.headers.Authorization = `Bearer ${_authToken}`;
  return cfg;
});

// ----------------------------------------------------------------
// Auto-retry on transient errors for idempotent GETs.
// ----------------------------------------------------------------
// Why: edge ingress / preview routing can briefly return 404/502/503/504
// when the upstream pod is hot-reloading or the route map is being
// refreshed. FastAPI itself NEVER 404s a valid registered path — when
// we DO see 404 with NO JSON `detail` body it is the edge layer. Two
// staggered retries (600 ms + 1500 ms) absorb up to ~2.5 s of flap.
// Strict guard rails:
//   - Only safe HTTP methods (GET / HEAD)
//   - Max 2 retries per request (no infinite loops, tracked via __retryCount)
//   - Only on 404/408/425/429/500/502/503/504 OR no `response` (network)
//   - 404 with a FastAPI JSON body (`{detail: "..."}` and a non-"Not Found"
//     message, or the id-not-found case) is treated as a REAL 404 from the
//     app → no retry. Only body-less / HTML 404s (= edge) are retried.
//   - 401/403 are NEVER retried — they are auth decisions.
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const EDGE_RETRY_STATUSES = new Set([404]);  // 404 with no JSON body only
const RETRY_DELAYS_MS = [600, 1500];

const is404FromEdge = (resp) => {
  if (!resp) return false;
  const ct = (resp.headers?.["content-type"] || "").toLowerCase();
  if (!ct.includes("application/json")) return true;
  const body = resp.data;
  // FastAPI 404 = `{detail: "Not Found"}` or `{detail: "profile_not_found"}`.
  // Those are authoritative "this record does not exist" — do NOT retry.
  if (body && typeof body === "object" && typeof body.detail === "string") return false;
  return true;
};

client.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const cfg = err?.config;
    if (!cfg) throw err;
    // Stamp the wall-clock at first observation so we can report `elapsed`
    // at the terminal-failure moment (after all retries have been exhausted).
    if (cfg.__startedAt == null) cfg.__startedAt = Date.now();
    const method = (cfg.method || "get").toLowerCase();
    const status = err?.response?.status;
    const noResponse = !err.response;
    const isTransientStatus = TRANSIENT_STATUSES.has(status);
    const isEdge404 = EDGE_RETRY_STATUSES.has(status) && is404FromEdge(err.response);
    const attempt = cfg.__retryCount || 0;
    // Eligibility for a retry attempt.
    const canRetryStatus = noResponse || isTransientStatus || isEdge404;
    const canRetryMethod = method === "get" || method === "head";
    if (canRetryStatus && canRetryMethod && attempt < RETRY_DELAYS_MS.length) {
      cfg.__retryCount = attempt + 1;
      await sleep(RETRY_DELAYS_MS[attempt]);
      return client.request(cfg);
    }
    // ---- Terminal failure: emit ONE structured line for copy-paste RCA ----
    // Scope: ONLY when the request was retry-eligible (network / transient /
    // edge-404) AND we've actually exhausted all retries. Non-retryable
    // statuses (401/403/legitimate 4xx, non-GET methods, FastAPI-body 404s)
    // propagate silently — they are not bugs we need the user to RCA.
    const isTerminalRetryFailure = canRetryStatus && canRetryMethod
      && attempt >= RETRY_DELAYS_MS.length;
    if (isTerminalRetryFailure) {
      try {
        const urlPath = (cfg.baseURL || "") + (cfg.url || "");
        const source = noResponse ? "network" : isEdge404 ? "edge/body-less" : "transient";
        // eslint-disable-next-line no-console
        console.error("[api] request failed", {
          endpoint: urlPath,
          method: method.toUpperCase(),
          status: status ?? "none",
          source,
          retries: attempt,
          elapsed_ms: Date.now() - cfg.__startedAt,
          timestamp: new Date().toISOString(),
        });
      } catch { /* never block error propagation */ }
    }
    throw err;
  },
);

export const api = {
  health: () => client.get("/health").then((r) => r.data),
  exchanges: () => client.get("/exchanges").then((r) => r.data),

  // ----- Institutional V2 Backtest PDFs (research / reporting) -----
  researchV2PdfMeta: () =>
    client.get("/research/v2_backtest_pdf_meta").then((r) => r.data),
  // Returns the FULL axios response (not just .data) so callers can
  // validate headers/status before triggering a download. The 60s
  // timeout reflects worst-case generation latency (matplotlib +
  // BTC kline fetch + reportlab) on a cold backend.
  researchV2PdfBlob: (multiplier, year) =>
    client.get("/research/v2_backtest_pdf", {
      params: { multiplier, year },
      responseType: "blob",
      timeout: 60000,
    }),
  // Native-fetch fallback used by the page only if the axios path
  // fails (e.g. transient ingress 502 inside axios's retry budget).
  // Uses the same bearer token that axios already injects.
  researchV2PdfBlobFallback: async (multiplier, year) => {
    const token = getAuthTokenForFallback();
    const url = `${API}/research/v2_backtest_pdf?multiplier=${encodeURIComponent(
      multiplier
    )}&year=${year}`;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(url, { headers, credentials: "omit" });
    if (!r.ok) {
      let detail = "";
      try {
        detail = await r.text();
      } catch {}
      const err = new Error(
        `HTTP ${r.status}${detail ? ` — ${detail.slice(0, 200)}` : ""}`
      );
      err.status = r.status;
      throw err;
    }
    const ctype = r.headers.get("content-type") || "";
    const blob = await r.blob();
    return { blob, contentType: ctype, status: r.status };
  },

  getConnection: () => client.get("/exchange/connection").then((r) => r.data),
  putConnection: (body) => client.put("/exchange/connection", body).then((r) => r.data),
  testConnection: (exchange) =>
    client.post("/exchange/test", null, exchange ? { params: { exchange } } : undefined).then((r) => r.data),
  // ----- User-scoped Binance Live credentials -----
  getBinanceLiveCredentials: () =>
    client.get("/exchange/credentials/binance_live").then((r) => r.data),
  saveBinanceLiveCredentials: (api_key, api_secret) =>
    client.post("/exchange/credentials/binance_live", { api_key, api_secret })
      .then((r) => r.data),
  deleteBinanceLiveCredentials: () =>
    client.delete("/exchange/credentials/binance_live").then((r) => r.data),
  // ----- User-scoped LBank credentials -----
  getLbankCredentials: () =>
    client.get("/exchange/credentials/lbank").then((r) => r.data),
  saveLbankCredentials: (api_key, api_secret) =>
    client.post("/exchange/credentials/lbank", { api_key, api_secret })
      .then((r) => r.data),
  deleteLbankCredentials: () =>
    client.delete("/exchange/credentials/lbank").then((r) => r.data),
  testnetRefreshHedgeMode: () => client.post("/exchange/testnet/refresh-hedge-mode").then((r) => r.data),
  testnetPreviewOrder: (body) => client.post("/exchange/testnet/preview-test-order", body).then((r) => r.data),
  testnetPlaceTestOrder: (body) => client.post("/exchange/testnet/place-test-order", body).then((r) => r.data),
  testnetClosePosition: (positionId) => client.post(`/exchange/testnet/close-position/${positionId}`).then((r) => r.data),
  testnetSyncPositions: () => client.post("/exchange/testnet/sync-positions").then((r) => r.data),
  testnetExecutionLogs: (limit = 50, filter = {}) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (filter.ok !== undefined) p.set("ok", String(Boolean(filter.ok)));
    if (filter.action) p.set("action", filter.action);
    return client.get(`/exchange/testnet/execution-logs?${p.toString()}`).then((r) => r.data);
  },

  // ----- Phase 1 Automated Validation Mode -----
  validationPhaseStatus: () => client.get("/validation-phase/status").then((r) => r.data),
  validationPhaseEnable: () => client.post("/validation-phase/enable").then((r) => r.data),
  validationPhaseDisable: () => client.post("/validation-phase/disable").then((r) => r.data),
  validationPhaseEvaluations: (limit = 20) =>
    client.get(`/validation-phase/evaluations?limit=${limit}`).then((r) => r.data),
  validationPhaseClearEvaluations: () =>
    client.delete("/validation-phase/evaluations").then((r) => r.data),

  // ----- Signal Engine observability -----
  signalEngineHeartbeat: () => client.get("/signal-engine/heartbeat").then((r) => r.data),
  signalEngineRestart: () => client.post("/signal-engine/restart").then((r) => r.data),

  // ----- Regime Gate preset cache (research-only) -----
  // `params` shape:
  //   { symbol, start_iso, end_iso, regime_timeframe,
  //     ema_fast, ema_slow, ema_slope_lookback, adx_length,
  //     adx_chop_threshold, adx_trend_threshold, adx_strong_threshold,
  //     preset_id?, preset_name? }
  //
  // Uses axios's native `params` option so encoding is delegated to a
  // single, audited path (no manual URLSearchParams concat). Axios
  // canonically encodes each value EXACTLY once; ISO strings with
  // colons survive as `start_iso=2026-03-29T00%3A00%3A00`. This is
  // the form FastAPI's query-param parser decodes back losslessly.
  // Empty / null / undefined values are stripped so we never emit
  // ambiguous `start_iso=` (which some FastAPI configs reject).
  regimeCacheStatus: (params) => {
    const cleaned = {};
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") cleaned[k] = v;
    });
    return client
      .get("/regime-cache/status", { params: cleaned })
      .then((r) => r.data);
  },
  regimeCachePrecompute: (payload) =>
    client.post("/regime-cache/precompute", payload).then((r) => r.data),


  // ----- Live strategy/setup trade tracker (read-only) -----
  liveStrategyTrades: (params = {}) => {
    const p = new URLSearchParams();
    if (params.status) p.set("status", params.status);
    if (params.setup_name) p.set("setup_name", params.setup_name);
    if (params.symbol) p.set("symbol", params.symbol);
    if (params.strategy_profile_id) p.set("strategy_profile_id", params.strategy_profile_id);
    if (params.limit) p.set("limit", String(params.limit));
    const qs = p.toString();
    return client.get(`/live-strategy-trades${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  liveStrategyTradesSummary: () =>
    client.get("/live-strategy-trades/summary").then((r) => r.data),
  liveStrategyTradesSummaryByVolatility: () =>
    client.get("/live-strategy-trades/summary-by-volatility").then((r) => r.data),
  liveStrategyTradesSummaryByPositioning: () =>
    client.get("/live-strategy-trades/summary-by-positioning").then((r) => r.data),

  // ----- Multi-Strategy Performance (groups by strategy_id, slot_id,
  // setup_name, symbol). Source = same live_strategy_trades collection.
  strategyPerformance: (params = {}) => {
    const p = new URLSearchParams();
    if (params.symbol)      p.set("symbol", params.symbol);
    if (params.strategy_id) p.set("strategy_id", params.strategy_id);
    if (params.slot_id)     p.set("slot_id", params.slot_id);
    if (params.status)      p.set("status", params.status);
    if (params.date_from)   p.set("date_from", params.date_from);
    if (params.date_to)     p.set("date_to", params.date_to);
    const qs = p.toString();
    return client.get(`/strategy-performance${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },

  // ----- Strategy Profiles -----
  strategyProfiles: () => client.get("/strategies/profiles").then((r) => r.data),
  strategyActive: () => client.get("/strategies/active").then((r) => r.data),
  strategyCreate: (body) => client.post("/strategies/profiles", body).then((r) => r.data),
  strategyUpdate: (id, body) => client.put(`/strategies/profiles/${id}`, body).then((r) => r.data),
  strategyDelete: (id) => client.delete(`/strategies/profiles/${id}`).then((r) => r.data),
  strategyActivate: (id) => client.post(`/strategies/profiles/${id}/activate`).then((r) => r.data),
  strategyDeactivate: () => client.post("/strategies/profiles/deactivate").then((r) => r.data),

  // ----- Strategy Slots (Multi-Strategy Architecture · Phase 2) -----
  // All trade execution attribution flows through slot_id / strategy_id /
  // strategy_profile_id. Production stays inert until `multi_strategy_enabled`.
  slotsList: () => client.get("/strategy-slots").then((r) => r.data),
  slotsGet: (id) => client.get(`/strategy-slots/${id}`).then((r) => r.data),
  slotsCreate: (body) => client.post("/strategy-slots", body).then((r) => r.data),
  slotsUpdate: (id, patch) => client.patch(`/strategy-slots/${id}`, patch).then((r) => r.data),
  slotsTransition: (id, target) => client.post(`/strategy-slots/${id}/state`, { target }).then((r) => r.data),
  slotsDelete: (id) => client.delete(`/strategy-slots/${id}`).then((r) => r.data),
  slotsFeatureFlag: () => client.get("/strategy-slots/_meta/feature-flag").then((r) => r.data),
  // Strategy Templates — operator-locked validated winners.
  // Production activation surface: pick template → slot created in DRAFT.
  strategyTemplatesList: () => client.get("/strategy-templates").then((r) => r.data),
  slotsFromTemplate: (template_id) => client.post("/strategy-slots/from-template", { template_id }).then((r) => r.data),
  // Multi-strategy flag toggle + preflight snapshot. The POST is
  // audit-logged backend-side and returns the resulting flag state
  // PLUS a preflight summary so the UI can render an after-flip banner.
  slotsSetFeatureFlag: (enabled) => client.post("/strategy-slots/_meta/feature-flag", { enabled }).then((r) => r.data),
  slotsPreflight: () => client.get("/strategy-slots/_meta/preflight").then((r) => r.data),
  slotsTelemetry: () => client.get("/strategy-slots/_meta/telemetry").then((r) => r.data),
  slotsHealth: () => client.get("/strategy-slots/_meta/health").then((r) => r.data),

  getSettings: () => client.get("/bot/settings").then((r) => r.data),
  putSettings: (body) => client.put("/bot/settings", body).then((r) => r.data),
  getActiveStrategy: () => client.get("/strategies/active").then((r) => r.data),
  debugLiveStatus: () => client.get("/debug/live-status").then((r) => r.data),
  status: () => client.get("/bot/status").then((r) => r.data),
  putBotSettings: (body) => client.put("/bot/settings", body).then((r) => r.data),
  start: () => client.post("/bot/start").then((r) => r.data),
  stop: () => client.post("/bot/stop").then((r) => r.data),
  pause: () => client.post("/bot/pause").then((r) => r.data),
  emergencyStop: () => client.post("/bot/emergency-stop").then((r) => r.data),
  closeAllPaper: () => client.post("/bot/close-all-paper").then((r) => r.data),
  closeAllReal: () => client.post("/bot/close-all-real").then((r) => r.data),

  getEntryRules: () => client.get("/strategy/entry-rules").then((r) => r.data),
  putEntryRules: (body) => client.put("/strategy/entry-rules", body).then((r) => r.data),
  getExitRules: () => client.get("/strategy/exit-rules").then((r) => r.data),
  putExitRules: (body) => client.put("/strategy/exit-rules", body).then((r) => r.data),

  getRisk: () => client.get("/risk/settings").then((r) => r.data),
  putRisk: (body) => client.put("/risk/settings", body).then((r) => r.data),
  toggleKillSwitch: (enabled) => client.post(`/risk/kill-switch/${enabled}`).then((r) => r.data),

  openPositions: () => client.get("/positions/open").then((r) => r.data),
  history: (limit = 100) => client.get(`/positions/history?limit=${limit}`).then((r) => r.data),
  closePosition: (id) => client.post(`/positions/${id}/close`).then((r) => r.data),

  // Live exchange snapshot — bypasses Mongo and queries the active
  // execution exchange (LBank/Binance Live) directly. Used by the
  // Dashboard in live modes so balance + open positions reflect the
  // exchange's source-of-truth, not stale local cache. Fail-safe shape:
  //   { available, reason, balance_usdt, open_positions[], source, fetched_at }
  liveSnapshot: () => client.get("/live/snapshot").then((r) => r.data),

  signalLogs: (limit = 200) => client.get(`/signals/logs?limit=${limit}`).then((r) => r.data),
  clearSignalLogs: () => client.delete("/signals/logs").then((r) => r.data),

  marketPrice: (symbol = "BTCUSDT") => client.get(`/market/price?symbol=${symbol}`).then((r) => r.data),
  liquidations: () => client.get("/market/liquidations").then((r) => r.data),

  // Backtest + walk-forward polling endpoints may coincide with a CPU-heavy
  // backtest task on the backend event loop. We keep the global 15 s timeout
  // strict for every other API, and extend ONLY these polling endpoints to
  // 60 s so they survive a slow tick without aborting.
  backtestRun: (body) => client.post("/backtest/run", body, { timeout: 60000 }).then((r) => r.data),
  backtestList: () => client.get("/backtest/list", { timeout: 60000 }).then((r) => r.data),
  backtestGet: (id, full = true) => client.get(`/backtest/${id}?full=${full}`, { timeout: 60000 }).then((r) => r.data),
  backtestCancel: (id) => client.post(`/backtest/${id}/cancel`, null, { timeout: 60000 }).then((r) => r.data),
  backtestDelete: (id) => client.delete(`/backtest/${id}`, { timeout: 60000 }).then((r) => r.data),
  backtestCsvUrl: (id) => `${API}/backtest/${id}/trades.csv`,

  // Composite Backtest — runs same cfg over N non-contiguous windows.
  backtestCompositeRun:    (body) => client.post("/backtest/composite/run", body, { timeout: 60000 }).then((r) => r.data),
  backtestCompositeList:   ()     => client.get("/backtest/composite/list", { timeout: 60000 }).then((r) => r.data),
  backtestCompositeGet:    (id)   => client.get(`/backtest/composite/${id}`, { timeout: 60000 }).then((r) => r.data),
  backtestCompositeDelete: (id)   => client.delete(`/backtest/composite/${id}`, { timeout: 60000 }).then((r) => r.data),

  // Bulk-cleanup endpoints — destructive delete by profit-factor threshold.
  // Always call `backtestCleanupPreview` before `backtestCleanup` so the
  // UI can show the user an accurate count in the confirmation dialog.
  backtestCleanupPreview: (profit_factor_lt) =>
    client.post("/backtest/cleanup/preview", { profit_factor_lt }).then((r) => r.data),
  backtestCleanup: (profit_factor_lt) =>
    client.post("/backtest/cleanup", { profit_factor_lt }).then((r) => r.data),

  // TF-scoped bulk delete (additive — coexists with PF cleanup above).
  // `filters` accepts `{ timeframes?: string[], profit_factor_below?: number }`.
  // At least one filter is required by the backend (returns 400 otherwise).
  backtestsBulkPreview: (filters) =>
    client.post("/backtests/bulk/preview", filters).then((r) => r.data),
  backtestsBulkDelete: (filters) =>
    client.delete("/backtests/bulk", { data: filters }).then((r) => r.data),

  walkforwardRun: (body) => client.post("/walkforward/run", body, { timeout: 60000 }).then((r) => r.data),
  walkforwardList: () => client.get("/walkforward/list", { timeout: 60000 }).then((r) => r.data),
  walkforwardGet: (id) => client.get(`/walkforward/${id}`, { timeout: 60000 }).then((r) => r.data),
  walkforwardCancel: (id) => client.post(`/walkforward/${id}/cancel`, null, { timeout: 60000 }).then((r) => r.data),
  walkforwardDelete: (id) => client.delete(`/walkforward/${id}`, { timeout: 60000 }).then((r) => r.data),
  // ---- Walk-Forward Setup Ablation ----
  walkforwardAblationRun:    (body) => client.post("/walkforward/ablation/run", body, { timeout: 60000 }).then((r) => r.data),
  walkforwardAblationList:   ()     => client.get("/walkforward/ablation/list", { timeout: 60000 }).then((r) => r.data),
  walkforwardAblationGet:    (id)   => client.get(`/walkforward/ablation/${id}`, { timeout: 60000 }).then((r) => r.data),
  walkforwardAblationDelete: (id)   => client.delete(`/walkforward/ablation/${id}`, { timeout: 60000 }).then((r) => r.data),

  // Research-job concurrency recovery (operator-driven).
  researchJobsStatus:         ()   => client.get("/research-jobs/status").then((r) => r.data),
  researchJobsCancelCurrent:  ()   => client.post("/research-jobs/cancel-current").then((r) => r.data),

  // Research-Window Kline Cache (Phase 1: klines only).
  researchWindowsCacheStatus: (preset_id, opts = {}) => {
    const params = new URLSearchParams({ preset_id });
    if (opts.symbol) params.set("symbol", opts.symbol);
    if (opts.strategy_profile_id) params.set("strategy_profile_id", opts.strategy_profile_id);
    if (opts.timeframes) params.set("timeframes", Array.isArray(opts.timeframes) ? opts.timeframes.join(",") : opts.timeframes);
    return client.get(`/research-windows/cache-status?${params.toString()}`).then((r) => r.data);
  },
  researchWindowsWarmCache:   (body) => client.post("/research-windows/warm-cache", body, { timeout: 180_000 }).then((r) => r.data),

  // Persistent research cache (Phase 2: disk + indicator cache).
  researchCacheTelemetry:     ()     => client.get("/research-cache/telemetry").then((r) => r.data),
  researchCacheClear:         (body) => client.post("/research-cache/clear", body || { scope: "all" }).then((r) => r.data),
  // Phase 3: Warm-all-presets operator flow.
  researchCacheWarmAll:        (body) => client.post("/research-cache/warm-all", body).then((r) => r.data),
  researchCacheWarmAllGet:     (jid)  => client.get(`/research-cache/warm-all/${jid}`).then((r) => r.data),
  researchCacheWarmAllCancel:  (jid)  => client.post(`/research-cache/warm-all/${jid}/cancel`).then((r) => r.data),
  // Dashboard observability — current EMA/ADX regime classification for the active profile.
  dashboardMarketRegime:       (symbol = "BTCUSDT", tf) =>
    client.get(`/dashboard/market-regime`, { params: { symbol, ...(tf ? { regime_timeframe: tf } : {}) } }).then((r) => r.data),
};

export const fmtUsd = (n, digits = 2) => {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const fmtPct = (n, digits = 2) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
};

export const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; }
};
